import React from "react";
import styles from "./style.module.scss";
import { RaceBuilderFormState } from "./RaceBuilderInputs";

export type RaceProfileLap = {
  lapNumber: number;
  energyTargetkWh: number;
  attack: boolean;
  tow: boolean;
};

export type RaceProfileData = {
  linearTargetkWh: number;
  totalAttackTimeS: number;
  attackLapDurationS: number | null;
  attackLapCount: number;
  laps: RaceProfileLap[];
};

export type RaceProfileDraft = RaceProfileData & {
  dataFile: string;
  totalRaceEnergykWh: number;
  numberOfLaps: number;
  ucProfile: boolean;
  lockingTargetkWh: number | "";
  maxUnderconsumptionkWh: number;
};

export function raceProfileInputsChanged(
  profile: RaceProfileDraft,
  inputs: RaceBuilderFormState,
): boolean {
  return (
    profile.dataFile !== inputs.dataFile.trim() ||
    profile.totalRaceEnergykWh !== inputs.totalRaceEnergykWh ||
    profile.numberOfLaps !== inputs.numberOfLaps ||
    profile.ucProfile !== inputs.ucProfile ||
    (inputs.ucProfile &&
      (profile.lockingTargetkWh !== inputs.lockingTargetkWh ||
        profile.maxUnderconsumptionkWh !== inputs.maxUnderconsumptionkWh))
  );
}

type RaceProfileProps = {
  profile: RaceProfileDraft | null;
  loading: boolean;
  error: string | null;
  canGenerate: boolean;
  inputsChanged: boolean;
  onGenerate: () => void;
  onToggle: (
    lapNumber: number,
    field: "attack" | "tow",
    checked: boolean,
  ) => void;
};

const energyFormat = new Intl.NumberFormat("en-GB", {
  maximumSignificantDigits: 6,
});

export default function RaceProfile({
  profile,
  loading,
  error,
  canGenerate,
  inputsChanged,
  onGenerate,
  onToggle,
}: RaceProfileProps) {
  const lapTargets = profile?.laps.map((lap) => lap.energyTargetkWh) ?? [];
  let remainingBudget = lapTargets.reduce((total, energy) => total + energy, 0);
  const remainingAfterLap = lapTargets.map((energy, index) => {
    remainingBudget -= energy;
    return index === lapTargets.length - 1 ? 0 : Math.max(0, remainingBudget);
  });
  return (
    <section
      className={`${styles.panel} ${styles.raceProfilePanel}`}
      aria-labelledby="race-profile-title"
      aria-busy={loading}
    >
      <div className={styles.panelHeader}>
        <h2 id="race-profile-title">Race Profile</h2>
      </div>

      <button
        type="button"
        className={styles.profileButton}
        onClick={onGenerate}
        disabled={!canGenerate || loading}
      >
        {loading ? "Generating..." : "Generate Profile"}
      </button>

      {error && (
        <div className={styles.errorMessage} role="alert">
          {error}
        </div>
      )}

      {inputsChanged && (
        <p className={styles.profileHint} role="status">
          Inputs have changed. Generate the profile to update the table.
        </p>
      )}
      {profile ? (
        <>
          <p className={styles.linearTarget} aria-live="polite">
            {profile.ucProfile ? "Base linear target" : "Linear target"}:{" "}
            {energyFormat.format(profile.linearTargetkWh)} kWh/lap
          </p>
          <div
            className={styles.profileTableContainer}
            role="region"
            aria-label="Scrollable race profile laps"
            tabIndex={0}
          >
            <table
              className={styles.profileTable}
              aria-label="Race profile laps"
            >
              <thead>
                <tr>
                  <th scope="col">Lap</th>
                  <th scope="col">Energy target (kWh)</th>
                  {profile.ucProfile && <th scope="col">Budget left (kWh)</th>}
                  <th scope="col">Attack</th>
                  <th scope="col">Tow</th>
                </tr>
              </thead>
              <tbody>
                {profile.laps.map((lap, index) => (
                  <tr key={lap.lapNumber}>
                    <th scope="row">{lap.lapNumber}</th>
                    <td>{energyFormat.format(lapTargets[index])}</td>
                    {profile.ucProfile && (
                      <td>{energyFormat.format(remainingAfterLap[index])}</td>
                    )}
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Attack lap ${lap.lapNumber}`}
                        checked={lap.attack}
                        disabled={loading}
                        onChange={(event) =>
                          onToggle(
                            lap.lapNumber,
                            "attack",
                            event.target.checked,
                          )
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Tow lap ${lap.lapNumber}`}
                        checked={lap.tow}
                        disabled={loading}
                        onChange={(event) =>
                          onToggle(lap.lapNumber, "tow", event.target.checked)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
}
